import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  setAdminPasscode,
  clearAdminPasscode,
  getAdminPasscode,
  formatApiError,
} from "../lib/api";
import {
  ShieldCheck,
  Check,
  X,
  ArrowLeft,
  LogOut,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
} from "lucide-react";

const STATUS_TABS = [
  { key: "pending", label: "Pending", icon: Clock },
  { key: "approved", label: "Approved", icon: CheckCircle2 },
  { key: "denied", label: "Denied", icon: XCircle },
];

export default function Admin() {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(Boolean(getAdminPasscode()));
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("pending");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);

  const loadUsers = async (status = tab) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/admin/users?status=${status}`, {
        _admin: true,
      });
      setUsers(data || []);
    } catch (err) {
      setError(formatApiError(err));
      if (err.response?.status === 401) {
        clearAdminPasscode();
        setUnlocked(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (unlocked) loadUsers(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, unlocked]);

  const onUnlock = async (e) => {
    e.preventDefault();
    setError("");
    setAdminPasscode(passcode.trim());
    try {
      await api.get("/admin/users?status=pending", { _admin: true });
      setUnlocked(true);
      setPasscode("");
    } catch (err) {
      clearAdminPasscode();
      setError(formatApiError(err) || "Invalid passcode");
    }
  };

  const onApprove = async (id) => {
    setActionId(id);
    try {
      await api.post(`/admin/users/${id}/approve`, {}, { _admin: true });
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setActionId(null);
    }
  };

  const onDeny = async (id) => {
    setActionId(id);
    try {
      await api.post(`/admin/users/${id}/deny`, {}, { _admin: true });
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setActionId(null);
    }
  };

  const onLock = () => {
    clearAdminPasscode();
    setUnlocked(false);
    setUsers([]);
  };

  if (!unlocked) {
    return (
      <div
        data-testid="admin-locked"
        className="min-h-screen flex items-center justify-center bg-brand text-brand-mint font-sans relative overflow-hidden"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 h-[520px] w-[1000px] rounded-full bg-brand-mint/18 blur-[140px]" />
        </div>
        <div className="relative w-full max-w-md mx-auto px-6">
          <button
            onClick={() => navigate("/login")}
            data-testid="admin-back-btn"
            className="mb-6 inline-flex items-center gap-2 text-sm text-brand-mint/70 hover:text-brand-mint"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to sign in
          </button>
          <form
            onSubmit={onUnlock}
            className="bg-brand-deep/50 backdrop-blur-xl border border-brand-mint/20 rounded-2xl p-8 space-y-5"
          >
            <div className="text-center">
              <div className="h-14 w-14 mx-auto rounded-2xl bg-brand-mint/15 border border-brand-mint/40 flex items-center justify-center text-brand-mint mb-4">
                <ShieldCheck className="h-7 w-7" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-brand-mint">
                Admin Access
              </h1>
              <p className="mt-2 text-sm text-brand-mint/70">
                Enter the passcode to manage account requests.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-xs uppercase tracking-widest text-brand-mint/65 font-medium">
                Passcode
              </label>
              <input
                type="password"
                required
                data-testid="admin-passcode-input"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="••••••••••"
                className="w-full rounded-lg border border-brand-mint/20 bg-brand-deeper/60 px-3 py-2.5 text-sm text-brand-mint placeholder:text-brand-mint/40 focus:outline-none focus:ring-2 focus:ring-brand-mint/40 focus:border-brand-mint/60"
              />
            </div>

            {error && (
              <div
                data-testid="admin-passcode-error"
                className="text-sm text-red-200 bg-red-500/15 border border-red-400/30 rounded-lg px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="admin-unlock-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-brand-mint hover:bg-brand-mintBright text-brand font-semibold transition-colors"
            >
              <ShieldCheck className="h-4 w-4" />
              Unlock
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="admin-page"
      className="min-h-screen bg-brand text-brand-mint font-sans relative overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-[-10%] right-[-10%] h-[420px] w-[640px] rounded-full bg-brand-mint/15 blur-[140px]" />
        <div className="absolute bottom-[-15%] left-[-10%] h-[420px] w-[640px] rounded-full bg-brand-mint/12 blur-[140px]" />
      </div>

      <header className="relative border-b border-brand-mint/15 bg-brand/70 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-brand-mint/15 border border-brand-mint/40 flex items-center justify-center text-brand-mint">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div>
              <h1 className="text-base font-semibold tracking-tight text-brand-mint">
                Admin Console
              </h1>
              <p className="text-xs text-brand-mint/60">
                Account request management
              </p>
            </div>
          </div>
          <button
            onClick={onLock}
            data-testid="admin-lock-btn"
            className="inline-flex items-center gap-2 text-sm text-brand-mint/75 hover:text-brand-mint border border-brand-mint/20 hover:border-brand-mint/50 rounded-lg px-3 py-1.5"
          >
            <LogOut className="h-4 w-4" />
            Lock
          </button>
        </div>
      </header>

      <main className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-brand-mint/70" />
            <h2 className="text-xl font-semibold tracking-tight text-brand-mint">
              Account requests
            </h2>
          </div>
          <div className="inline-flex p-1 rounded-xl border border-brand-mint/15 bg-brand-deep/50">
            {STATUS_TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`admin-tab-${t.key}`}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? "bg-brand-mint text-brand"
                      : "text-brand-mint/75 hover:text-brand-mint"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div
            data-testid="admin-error"
            className="mb-4 text-sm text-red-200 bg-red-500/15 border border-red-400/30 rounded-lg px-3 py-2"
          >
            {error}
          </div>
        )}

        <div className="bg-brand-deep/40 backdrop-blur-xl border border-brand-mint/15 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="px-6 py-10 text-center text-brand-mint/60 text-sm">
              Loading…
            </div>
          ) : users.length === 0 ? (
            <div
              data-testid="admin-empty"
              className="px-6 py-12 text-center text-brand-mint/55 text-sm"
            >
              No {tab} requests.
            </div>
          ) : (
            <ul className="divide-y divide-brand-mint/10">
              {users.map((u) => (
                <li
                  key={u.id}
                  data-testid={`admin-user-row-${u.id}`}
                  className="px-5 py-4 flex items-center justify-between gap-4"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-brand-mint truncate">
                      {u.name || "—"}
                    </p>
                    <p className="text-xs text-brand-mint/65 truncate">
                      {u.email}
                    </p>
                    <p className="text-[11px] text-brand-mint/45 mt-1">
                      Requested {new Date(u.created_at).toLocaleString()}
                    </p>
                  </div>
                  {tab === "pending" && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => onApprove(u.id)}
                        disabled={actionId === u.id}
                        data-testid={`admin-approve-${u.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-brand-mint hover:bg-brand-mintBright text-brand transition-colors disabled:opacity-50"
                      >
                        <Check className="h-4 w-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => onDeny(u.id)}
                        disabled={actionId === u.id}
                        data-testid={`admin-deny-${u.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-red-400/40 text-red-200 hover:bg-red-500/15 transition-colors disabled:opacity-50"
                      >
                        <X className="h-4 w-4" />
                        Deny
                      </button>
                    </div>
                  )}
                  {tab !== "pending" && (
                    <span className="text-xs uppercase tracking-widest text-brand-mint/55">
                      {u.status}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
